import { useState } from 'react';
import { useAdminCoupons, useCreateCoupon, useUpdateCoupon, useDeleteCoupon } from '@/hooks/useAdmin';
import { useToast } from '@/contexts/ToastContext';
import type { Coupon, CreateCouponInput } from '@/services/admin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

const EMPTY_FORM: CreateCouponInput = {
  code: '',
  description: '',
  percentOff: null,
  freeShipping: false,
  waiveMinimum: false,
  maxUses: null,
  expiresAt: null,
};

export const AdminCouponsPage = () => {
  const { data: coupons, isLoading } = useAdminCoupons();
  const createCoupon = useCreateCoupon();
  const updateCoupon = useUpdateCoupon();
  const deleteCoupon = useDeleteCoupon();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateCouponInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const toast = useToast();

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const hasBenefit = form.percentOff !== null || form.freeShipping || form.waiveMinimum;

  const handleCreate = async () => {
    setFormError(null);
    if (!form.code.trim()) {
      setFormError('Code is required');
      return;
    }
    if (!hasBenefit) {
      setFormError('Enable at least one benefit');
      return;
    }
    try {
      await createCoupon.mutateAsync(form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success('Coupon created');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create coupon');
    }
  };

  const handleToggleActive = (coupon: Coupon) => {
    updateCoupon.mutate(
      { code: coupon.code, updates: { isActive: !coupon.isActive } },
      {
        onSuccess: () => toast.success(`Coupon ${coupon.isActive ? 'deactivated' : 'activated'}`),
        onError: () => toast.error('Failed to update coupon'),
      },
    );
  };

  const handleDelete = (code: string) => {
    deleteCoupon.mutate(code, {
      onSuccess: () => {
        setConfirmDelete(null);
        toast.success('Coupon deleted');
      },
      onError: () => toast.error('Failed to delete coupon'),
    });
  };

  const sorted = [...(coupons ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div>
      <div className="admin-page-header" style={{ marginBottom: 'var(--space-6)' }}>
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Coupons
          </h1>
          <p
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            Create and manage discount codes for checkout.
          </p>
        </div>
        <Button
          className="responsive-button"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'New Coupon'}
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            padding: 'var(--space-6)',
            marginBottom: 'var(--space-6)',
          }}
        >
          <h2 style={{ ...sectionTitleStyle, marginBottom: 'var(--space-4)' }}>New Coupon</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: '500px' }}>
            {/* Code */}
            <div>
              <label style={labelStyle}>Code</label>
              <input
                className="admin-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. FREESHIP2024"
                style={{ fontSize: 'var(--text-sm)', textTransform: 'uppercase' }}
              />
              <p style={hintStyle}>Customers will enter this at checkout</p>
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description (internal)</label>
              <input
                className="admin-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Launch promotion - 10% off"
                style={{ fontSize: 'var(--text-sm)' }}
              />
            </div>

            {/* Benefits toggles */}
            <div>
              <label style={{ ...labelStyle, marginBottom: 'var(--space-2)' }}>Benefits</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Percentage off */}
                <div className="flex items-center" style={{ gap: 'var(--space-3)' }}>
                  <input
                    type="checkbox"
                    checked={form.percentOff !== null}
                    onChange={(e) =>
                      setForm({ ...form, percentOff: e.target.checked ? 10 : null })
                    }
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div className="flex items-center" style={{ gap: 'var(--space-2)', flex: 1 }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                      Percentage off
                    </span>
                    {form.percentOff !== null && (
                      <div className="flex items-center" style={{ gap: '2px' }}>
                        <input
                          className="admin-input"
                          type="number"
                          min="1"
                          max="100"
                          value={form.percentOff}
                          onChange={(e) =>
                            setForm({ ...form, percentOff: parseInt(e.target.value, 10) || 1 })
                          }
                          style={{ width: '60px', fontSize: 'var(--text-sm)', textAlign: 'right' }}
                        />
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Free shipping */}
                <label className="flex items-center" style={{ gap: 'var(--space-3)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.freeShipping}
                    onChange={(e) => setForm({ ...form, freeShipping: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                    Free shipping
                  </span>
                </label>

                {/* Waive minimum */}
                <label className="flex items-center" style={{ gap: 'var(--space-3)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.waiveMinimum}
                    onChange={(e) => setForm({ ...form, waiveMinimum: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                    Waive $5 minimum order
                  </span>
                </label>
              </div>
            </div>

            {/* Limits */}
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 'var(--space-4)' }}>
              <div>
                <label style={labelStyle}>Max uses</label>
                <input
                  className="admin-input"
                  type="number"
                  min="1"
                  value={form.maxUses ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, maxUses: e.target.value ? parseInt(e.target.value, 10) : null })
                  }
                  placeholder="Unlimited"
                  style={{ fontSize: 'var(--text-sm)' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Expires</label>
                <input
                  className="admin-input"
                  type="date"
                  value={form.expiresAt ? form.expiresAt.split('T')[0] : ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expiresAt: e.target.value ? new Date(e.target.value + 'T23:59:59Z').toISOString() : null,
                    })
                  }
                  style={{ fontSize: 'var(--text-sm)' }}
                />
              </div>
            </div>

            {formError && (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-500)' }}>{formError}</p>
            )}

            <Button onClick={handleCreate} disabled={createCoupon.isPending}>
              {createCoupon.isPending ? 'Creating...' : 'Create Coupon'}
            </Button>
          </div>
        </div>
      )}

      {/* Coupons List */}
      {sorted.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--space-12)',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          No coupons yet. Click "New Coupon" to create one.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {sorted.map((coupon) => (
            <CouponCard
              key={coupon.code}
              coupon={coupon}
              onToggleActive={() => handleToggleActive(coupon)}
              onDelete={() => setConfirmDelete(coupon.code)}
              isUpdating={updateCoupon.isPending}
            />
          ))}
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete coupon"
          message={`Are you sure you want to delete coupon "${confirmDelete}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Keep"
          onConfirm={() => handleDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          isPending={deleteCoupon.isPending}
        />
      )}
    </div>
  );
};

const CouponCard = ({
  coupon,
  onToggleActive,
  onDelete,
  isUpdating,
}: {
  readonly coupon: Coupon;
  readonly onToggleActive: () => void;
  readonly onDelete: () => void;
  readonly isUpdating: boolean;
}) => {
  const isExpired = coupon.expiresAt && new Date(coupon.expiresAt) < new Date();
  const isMaxed = coupon.maxUses !== null && coupon.usedCount >= coupon.maxUses;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        padding: 'var(--space-4) var(--space-5)',
        opacity: coupon.isActive && !isExpired && !isMaxed ? 1 : 0.7,
      }}
    >
      <div>
        {/* Top row: Code + status + actions */}
        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-1)', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--text-primary)',
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
              }}
            >
              {coupon.code}
            </span>
            <StatusBadge active={coupon.isActive} expired={!!isExpired} maxed={!!isMaxed} />
          </div>

          {/* Actions */}
          <div className="flex items-center" style={{ gap: 'var(--space-1)' }}>
            <button
              onClick={onToggleActive}
              disabled={isUpdating}
              style={{
                fontSize: 'var(--text-xs)',
                color: coupon.isActive ? 'var(--color-warning-600)' : 'var(--color-success-600)',
                background: 'none',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                padding: 'var(--space-1) var(--space-3)',
                minHeight: '32px',
              }}
            >
              {coupon.isActive ? 'Deactivate' : 'Activate'}
            </button>
            <button
              onClick={onDelete}
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-error-500)',
                background: 'none',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                padding: 'var(--space-1) var(--space-3)',
                minHeight: '32px',
              }}
            >
              Delete
            </button>
          </div>
        </div>

        {coupon.description && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
            {coupon.description}
          </p>
        )}

        {/* Benefits */}
        <div className="flex" style={{ gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
          {coupon.percentOff && <Pill>{coupon.percentOff}% off</Pill>}
          {coupon.freeShipping && <Pill>Free shipping</Pill>}
          {coupon.waiveMinimum && <Pill>No minimum</Pill>}
        </div>

        {/* Stats */}
        <div className="flex" style={{ gap: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', flexWrap: 'wrap' }}>
          <span>
            Used: {coupon.usedCount}{coupon.maxUses !== null ? ` / ${coupon.maxUses}` : ''}
          </span>
          {coupon.expiresAt && (
            <span>
              Exp: {new Date(coupon.expiresAt).toLocaleDateString()}
            </span>
          )}
          <span>{new Date(coupon.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({
  active,
  expired,
  maxed,
}: {
  readonly active: boolean;
  readonly expired: boolean;
  readonly maxed: boolean;
}) => {
  let label = 'Active';
  let color = 'var(--color-success-600)';
  let bg = 'var(--bg-success-subtle, rgba(34,197,94,0.1))';

  if (!active) {
    label = 'Inactive';
    color = 'var(--text-tertiary)';
    bg = 'var(--bg-surface-sunken)';
  } else if (expired) {
    label = 'Expired';
    color = 'var(--color-warning-600)';
    bg = 'var(--bg-warning-subtle, rgba(234,179,8,0.1))';
  } else if (maxed) {
    label = 'Limit reached';
    color = 'var(--color-warning-600)';
    bg = 'var(--bg-warning-subtle, rgba(234,179,8,0.1))';
  }

  return (
    <span
      style={{
        fontSize: '0.65rem',
        fontWeight: 'var(--font-weight-semibold)',
        color,
        backgroundColor: bg,
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </span>
  );
};

const Pill = ({ children }: { readonly children: React.ReactNode }) => (
  <span
    style={{
      fontSize: 'var(--text-xs)',
      color: 'var(--text-accent)',
      backgroundColor: 'var(--bg-accent-subtle, rgba(59,130,246,0.1))',
      padding: '2px 8px',
      borderRadius: 'var(--radius-full)',
      fontWeight: 'var(--font-weight-medium)',
    }}
  >
    {children}
  </span>
);

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-primary)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--text-tertiary)',
  display: 'block',
  marginBottom: '2px',
};

const hintStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--text-tertiary)',
  marginTop: '2px',
};
